/**
 * Plain-English text and small pure helpers for the Deals admin screens
 * (F-DEAL-N1..N5). Kept free of React so it can be unit-tested.
 */
import type { AutoDiscountRule } from '@/lib/autoDiscounts'
import { describeDays, formatMinute } from '@/lib/menuRules'
import type { ComboSlotView, ComboComponentView } from '@/types/deals'

export type Money = (v: number) => string

export interface NameLookup {
  product: (id: string) => string | undefined
  category: (id: string) => string | undefined
}

/** 0 = Sunday … 6 = Saturday, shown Monday first. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const
export const DAY_LETTER: Record<number, string> = { 0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' }

/** 750 → "12:30" (for <input type="time">). */
export function minuteToHHMM(m: number | null | undefined): string {
  if (m == null || !Number.isFinite(m)) return ''
  const h = Math.floor(m / 60)
  const mm = m % 60
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

/** "12:30" → 750; '' / junk → null. */
export function hhmmToMinute(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((v ?? '').trim())
  if (!m) return null
  const h = Number(m[1])
  const mm = Number(m[2])
  if (h > 23 || mm > 59) return null
  return h * 60 + mm
}

/** True when the window crosses midnight (22:00 – 02:00). */
export function isOvernight(start: number | null, end: number | null): boolean {
  return start != null && end != null && end < start
}

/** "7:00 PM – 2:00 AM (next day)" */
export function describeTimeRange(start: number | null, end: number | null): string {
  if (start == null || end == null) return 'All day'
  return `${formatMinute(start)} – ${formatMinute(end)}${isOvernight(start, end) ? ' (next day)' : ''}`
}

/** Joins names as "A, B or C". */
export function joinOr(names: string[]): string {
  const n = names.filter(Boolean)
  if (n.length <= 1) return n[0] ?? ''
  return `${n.slice(0, -1).join(', ')} or ${n[n.length - 1]}`
}

function targetNames(productIds: readonly string[], categoryIds: readonly string[], names: NameLookup): string {
  const out: string[] = []
  for (const c of categoryIds ?? []) out.push(names.category(c) ?? 'a removed category')
  for (const p of productIds ?? []) out.push(names.product(p) ?? 'a removed item')
  return joinOr(out)
}

type RuleShape = Pick<
  AutoDiscountRule,
  | 'kind'
  | 'buy_qty'
  | 'get_qty'
  | 'get_percent'
  | 'bundle_qty'
  | 'bundle_price'
  | 'min_subtotal'
  | 'reward_type'
  | 'reward_value'
  | 'reward_product_id'
  | 'max_discount'
  | 'buy_product_ids'
  | 'buy_category_ids'
  | 'get_product_ids'
  | 'get_category_ids'
>

/**
 * "Buy 1 get 1 free on Pizza", "2 × Pizza for Rs 1,800",
 * "10% off when you spend Rs 2,000 (max Rs 500)", "Free Pepsi over Rs 1,000".
 */
export function describeAutoDiscountRule(r: RuleShape, names: NameLookup, money: Money): string {
  switch (r.kind) {
    case 'buy_x_get_y': {
      const buy = targetNames(r.buy_product_ids, r.buy_category_ids, names) || 'anything'
      const pct = Number(r.get_percent) || 0
      const off = pct >= 100 ? 'free' : `${pct}% off`
      const sameSet = (r.get_product_ids?.length ?? 0) === 0 && (r.get_category_ids?.length ?? 0) === 0
      if (sameSet) return `Buy ${r.buy_qty} get ${r.get_qty} ${off} on ${buy}`
      const get = targetNames(r.get_product_ids, r.get_category_ids, names)
      return `Buy ${r.buy_qty} × ${buy}, get ${r.get_qty} × ${get} ${off}`
    }
    case 'multi_buy': {
      const on = targetNames(r.buy_product_ids, r.buy_category_ids, names) || 'any item'
      return `${r.bundle_qty} × ${on} for ${money(r.bundle_price)}`
    }
    case 'spend_over': {
      const scope = targetNames(r.buy_product_ids, r.buy_category_ids, names)
      const spend = `${money(r.min_subtotal)}${scope ? ` on ${scope}` : ''}`
      if (r.reward_type === 'percent') {
        const cap = r.max_discount > 0 ? ` (max ${money(r.max_discount)})` : ''
        return `${r.reward_value}% off when you spend ${spend}${cap}`
      }
      if (r.reward_type === 'amount') return `${money(r.reward_value)} off when you spend ${spend}`
      if (r.reward_type === 'free_item') {
        const item = names.product(r.reward_product_id) ?? 'an item'
        const cap = r.max_discount > 0 ? ` (max ${money(r.max_discount)})` : ''
        return `Free ${item} over ${spend}${cap}`
      }
      return `Spend over ${spend}`
    }
    default:
      return 'Unknown deal type'
  }
}

export interface OrderTypeLabel {
  id: string
  label: string
}

/** "Every day · All day · Till & online · All order types" */
export function describeWhen(
  r: Pick<AutoDiscountRule, 'days' | 'start_minute' | 'end_minute' | 'valid_from' | 'valid_to' | 'channels' | 'order_types'>,
  orderTypeLabel: (id: string) => string,
): string {
  const parts: string[] = []
  parts.push(describeDays(r.days ?? []))
  parts.push(describeTimeRange(r.start_minute, r.end_minute))
  if (r.valid_from && r.valid_to) parts.push(`${r.valid_from} to ${r.valid_to}`)
  else if (r.valid_from) parts.push(`from ${r.valid_from}`)
  else if (r.valid_to) parts.push(`until ${r.valid_to}`)
  const ch = (r.channels ?? []).map((c) => c.toLowerCase())
  if (ch.length === 1) parts.push(ch[0] === 'online' ? 'Online only' : 'Till only')
  else parts.push('Till & online')
  const ots = r.order_types ?? []
  parts.push(ots.length === 0 ? 'All order types' : ots.map(orderTypeLabel).join(', '))
  return parts.join(' · ')
}

/** "Zinger Burger + choice of side + choice of drink" */
export function describeComboIncludes(components: ComboComponentView[], slots: ComboSlotView[]): string {
  const parts: string[] = []
  for (const c of components) parts.push(c.quantity > 1 ? `${c.quantity} × ${c.name}` : c.name)
  for (const s of slots) {
    const label = s.name.replace(/^choose\s+(a|an|your)?\s*/i, '').trim() || s.name
    // "Choose 2 drinks" already says how many; only add the count when the
    // name does not ("Drinks", pick 2 -> "choice of 2 drinks").
    const counted = /^\d/.test(label) || s.choose <= 1 ? label : `${s.choose} ${label}`
    parts.push(`choice of ${counted.toLowerCase()}`)
  }
  return parts.join(' + ')
}

export interface DraftComponent {
  product_id: string
  quantity: number
}
export interface DraftOption {
  option_id?: string
  product_id: string
  price_delta: number
}
export interface DraftSlot {
  group_id?: string
  name: string
  choose: number
  options: DraftOption[]
}

/**
 * What the guest would pay buying the parts separately: fixed items at their
 * own price × qty, plus the cheapest option of each choice × how many they pick.
 * Mirrors the server's separate_price.
 */
export function separatePrice(
  components: DraftComponent[],
  slots: DraftSlot[],
  priceOf: (productId: string) => number | undefined,
): number {
  let total = 0
  for (const c of components) {
    if (!c.product_id) continue
    total += (priceOf(c.product_id) ?? 0) * Math.max(1, c.quantity || 1)
  }
  for (const s of slots) {
    const prices = s.options
      .filter((o) => o.product_id)
      .map((o) => priceOf(o.product_id))
      .filter((p): p is number => typeof p === 'number')
    if (prices.length === 0) continue
    total += Math.min(...prices) * Math.max(1, s.choose || 1)
  }
  return Math.round(total * 100) / 100
}

/** Client-side mirror of the server's combo checks — catches the obvious before a round trip. */
export function comboDraftProblem(components: DraftComponent[], slots: DraftSlot[]): string | null {
  const comps = components.filter((c) => c.product_id)
  if (comps.length === 0 && slots.length === 0) return 'Add at least one item or choice to the deal'
  if (components.some((c) => !c.product_id)) return 'Pick an item for every row'
  const seen = new Set<string>()
  for (const c of comps) {
    if (seen.has(c.product_id)) return 'An item is listed twice — change its quantity instead'
    seen.add(c.product_id)
    if (!(c.quantity >= 1 && c.quantity <= 50)) return 'Quantity must be between 1 and 50'
  }
  for (const s of slots) {
    if (!s.name.trim()) return 'Name every choice (for example "Choose a drink")'
    if (!(s.choose >= 1 && s.choose <= 10)) return 'Each choice must pick between 1 and 10 items'
    if (s.options.length === 0) return `Add at least one option to "${s.name.trim()}"`
    if (s.options.some((o) => !o.product_id)) return `Pick an item for every option in "${s.name.trim()}"`
    const opt = new Set<string>()
    for (const o of s.options) {
      if (opt.has(o.product_id)) return `An item is listed twice in "${s.name.trim()}"`
      opt.add(o.product_id)
      if (o.price_delta < 0) return 'An upgrade price cannot be negative'
    }
  }
  return null
}

export interface DraftWindow {
  days: number[]
  start_minute: number | null
  end_minute: number | null
}

/** Mirrors the server's price-rule checks. */
export function priceRuleProblem(r: { price: number | null; order_types: string[] } & DraftWindow): string | null {
  if (r.price == null || !Number.isFinite(r.price) || r.price < 0) return 'Each special price needs a price of 0 or more'
  if ((r.start_minute == null) !== (r.end_minute == null)) return 'Set both a start and an end time, or neither'
  if (r.start_minute != null && r.start_minute === r.end_minute) return 'Start and end time cannot be the same'
  const everyDay = r.days.length === 0 || r.days.length === 7
  if (r.order_types.length === 0 && everyDay && r.start_minute == null)
    return 'A special price needs an order type, days or hours — otherwise change the item’s price'
  return null
}

/** Mirrors the server's availability-window checks (times required). */
export function windowProblem(w: DraftWindow): string | null {
  if (w.start_minute == null || w.end_minute == null) return 'Set a start and an end time for every time window'
  if (w.start_minute === w.end_minute) return 'Start and end time cannot be the same'
  return null
}

/** Error text from an API failure (ApiClientError carries the server message). */
export function errorText(e: unknown, fallback = 'Something went wrong'): string {
  if (e instanceof Error && e.message) return e.message
  if (typeof e === 'string' && e) return e
  return fallback
}
